import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ContractDurationType,
  type ContractDurationDto,
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
} from "../../../../../autogen/bff-api";
import { ThemedDatePicker } from "../../../../../common/input/DateTimeSelectors/ThemedDatePicker";
import { NumericInput } from "../../../../../common/input/NumericInput";
import { SelectableCard } from "../../../../../common/input/SelectableCard";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { FieldSaveError } from "./FieldSaveError";

interface Props {
  eventId: string;
}

type EndType = "DATE" | "MONTHS";

// Denormalized intentionally: keeping inactive variant fields preserves user input
// across hasDefinedEnd / endType toggles. The single-source projection to a DTO
// happens in buildDuration().
interface ContractDurationDraft {
  startDate: string | null;
  endDate: string | null;
  periodMonths: number | null;
  hasDefinedEnd: boolean;
  endType: EndType;
}

const EMPTY_DRAFT: ContractDurationDraft = {
  startDate: null,
  endDate: null,
  periodMonths: null,
  hasDefinedEnd: false,
  endType: "DATE",
};

const buildDuration = (draft: ContractDurationDraft): ContractDurationDto | null => {
  if (!draft.hasDefinedEnd) {
    return {
      type: ContractDurationType.OpenEnded,
      startDate: draft.startDate,
      endDate: null,
      periodMonths: null,
    };
  }
  if (draft.endType === "DATE") {
    if (!draft.startDate || !draft.endDate) return null;
    return {
      type: ContractDurationType.FixedDates,
      startDate: draft.startDate,
      endDate: draft.endDate,
      periodMonths: null,
    };
  }
  if (draft.periodMonths == null || draft.periodMonths < 1) return null;
  return {
    type: ContractDurationType.PeriodBased,
    startDate: draft.startDate,
    endDate: null,
    periodMonths: draft.periodMonths,
  };
};

const buildDraft = (duration?: ContractDurationDto | null): ContractDurationDraft => {
  if (!duration) return EMPTY_DRAFT;
  switch (duration.type) {
    case ContractDurationType.OpenEnded:
      return {
        startDate: duration.startDate ?? null,
        endDate: null,
        periodMonths: null,
        hasDefinedEnd: false,
        endType: "DATE",
      };
    case ContractDurationType.FixedDates:
      return {
        startDate: duration.startDate ?? null,
        endDate: duration.endDate ?? null,
        periodMonths: null,
        hasDefinedEnd: true,
        endType: "DATE",
      };
    case ContractDurationType.PeriodBased:
      return {
        startDate: duration.startDate ?? null,
        endDate: null,
        periodMonths: duration.periodMonths ?? null,
        hasDefinedEnd: true,
        endType: "MONTHS",
      };
  }
  return EMPTY_DRAFT;
};

export const ContractDurationField = ({ eventId }: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const duration = state.eventData.contractDuration;
  const [draft, setDraft] = useState<ContractDurationDraft>(() => buildDraft(duration));
  const previousEventId = useRef(eventId);

  // Only re-init the draft on event change; never on self-echoed save round-trips.
  // Re-syncing on every `duration` change would drop user input for inactive
  // variants when the toggle hides them.
  useEffect(() => {
    if (previousEventId.current !== eventId) {
      previousEventId.current = eventId;
      setDraft(buildDraft(duration));
    }
  }, [duration, eventId]);

  const saving = fieldStatus("eventData.contractDuration");
  const isSaving = saving === "saving" || saving === "pending";

  const save = (next: ContractDurationDraft) => {
    setDraft(next);
    const dto = buildDuration(next);
    if (dto === null) {
      if (duration != null) {
        updateField({ eventData: { contractDuration: null } });
      }
      return;
    }
    updateField({ eventData: { contractDuration: dto } });
  };

  const showEndDateRequired = draft.hasDefinedEnd && draft.endType === "DATE" && !draft.endDate;
  const showMonthsRequired = draft.hasDefinedEnd && draft.endType === "MONTHS" && draft.periodMonths == null;
  const showMonthsTooSmall =
    draft.hasDefinedEnd && draft.endType === "MONTHS" && draft.periodMonths != null && draft.periodMonths < 1;

  return (
    <FormControl mb="8">
      <Stack spacing="5" align="stretch">
        <Box>
          <Flex align="center" gap="2" mb="2">
            <Text fontSize="sm" fontWeight="medium" color="smText">
              {t("Start date")}
            </Text>
            {isSaving && <Spinner size="xs" />}
          </Flex>
          <ThemedDatePicker
            defaultDate={draft.startDate ?? undefined}
            allowPastDates
            onChange={(date) => save({ ...draft, startDate: date ?? null })}
          />
        </Box>

        <Flex align="center" justify="space-between" gap="4">
          <FormLabel mb="0">{t("Has the contract a defined end?")}</FormLabel>
          <ButtonGroup isAttached size="sm">
            <Button
              colorScheme={draft.hasDefinedEnd ? "teal" : "gray"}
              variant={draft.hasDefinedEnd ? "solid" : "outline"}
              onClick={() => save({ ...draft, hasDefinedEnd: true })}
            >
              {t("Yes")}
            </Button>
            <Button
              colorScheme={!draft.hasDefinedEnd ? "teal" : "gray"}
              variant={!draft.hasDefinedEnd ? "solid" : "outline"}
              onClick={() => save({ ...draft, hasDefinedEnd: false })}
            >
              {t("No")}
            </Button>
          </ButtonGroup>
        </Flex>

        {draft.hasDefinedEnd && (
          <RadioGroup
            value={draft.endType}
            onChange={(v) => save({ ...draft, endType: v as EndType })}
          >
            <Stack spacing="2">
              <SelectableCard
                as="label"
                isSelected={draft.endType === "DATE"}
                px="4"
                py="3"
                cursor="pointer"
              >
                <Flex align="center" gap="3">
                  <Radio value="DATE" colorScheme="teal" />
                  <Text fontWeight="medium" fontSize="sm">
                    {t("Specific end date")}
                  </Text>
                </Flex>
              </SelectableCard>
              {draft.endType === "DATE" && (
                <Box pl={{ base: "2", sm: "8" }} pb="1">
                  <ThemedDatePicker
                    defaultDate={draft.endDate ?? undefined}
                    allowPastDates
                    onChange={(date) => save({ ...draft, endDate: date ?? null })}
                  />
                  {showEndDateRequired && (
                    <Text fontSize="xs" color="smError" mt="1">
                      {t("End date is required")}
                    </Text>
                  )}
                </Box>
              )}

              <SelectableCard
                as="label"
                isSelected={draft.endType === "MONTHS"}
                px="4"
                py="3"
                cursor="pointer"
              >
                <Flex align="center" gap="3">
                  <Radio value="MONTHS" colorScheme="teal" />
                  <Text fontWeight="medium" fontSize="sm">
                    {t("Number of months")}
                  </Text>
                </Flex>
              </SelectableCard>
              {draft.endType === "MONTHS" && (
                <Box pl={{ base: "2", sm: "8" }} pb="1">
                  <NumericInput
                    numericValue={draft.periodMonths}
                    onNumericChange={(n) => save({ ...draft, periodMonths: n })}
                    min={1}
                    maxW={{ base: "full", sm: "200px" }}
                  />
                  {showMonthsRequired && (
                    <Text fontSize="xs" color="smError" mt="1">
                      {t("Number of months is required")}
                    </Text>
                  )}
                  {showMonthsTooSmall && (
                    <Text fontSize="xs" color="smError" mt="1">
                      {t("Must be at least 1 month")}
                    </Text>
                  )}
                </Box>
              )}
            </Stack>
          </RadioGroup>
        )}
      </Stack>
      <FieldSaveError eventId={eventId} path="eventData.contractDuration" />
    </FormControl>
  );
};
